import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, PermissionStatus } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Browser } from '@capacitor/browser';
import { App, AppState } from '@capacitor/app';
import { BehaviorSubject } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import { getServerDeviceId } from '../utils/push-device.utils';
import { TopicApiService } from './topic/topic-api.service';
import { NotificationNavigationService } from './notification/notification-navigation.service';
import { NotificationStateService } from './notification/notification-state.service';
import { InboxNotification, NotificationType, NotificationCategory } from '../models/core/inbox-notification.model';
import { NotificationBadgeService } from './notification-badge.service';
import { SKIP_UNAUTHORIZED_LOGOUT } from '../core/auth.interceptor';

export interface InAppNotification {
  title: string;
  body: string;
  data?: any;
  timestamp: Date;
  unreadCount?: number;
  notificationId?: string;
}

@Injectable({ providedIn: 'root' })
export class FirebaseMessagingService {
  private hasInitialized = false;
  private notificationSubject = new BehaviorSubject<InAppNotification | null>(null);
  public notification$ = this.notificationSubject.asObservable();
  private pendingNotificationData: any = null;
  private isProcessingInitialNotification = false;
  private initialNotificationProcessed = false;
  private readonly markedAsReadFromPush = new Set<number>();

  constructor(
    private readonly platform: Platform,
    private readonly logger: NGXLogger,
    private readonly ngZone: NgZone,
    private readonly topicApiService: TopicApiService,
    private readonly notificationNavigation: NotificationNavigationService,
    private readonly router: Router,
    private readonly notificationState: NotificationStateService,
    // NotificationBadgeService depends on this service, so it is resolved lazily to avoid a DI cycle.
    private readonly injector: Injector
  ) { }

  async initialize(): Promise<void> {
    if (this.hasInitialized) {
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      this.logger.debug('FirebaseMessaging: no nativo, se omite inicialización');
      this.hasInitialized = true;
      return;
    }

    this.isProcessingInitialNotification = false;
    this.initialNotificationProcessed = false;

    await this.platform.ready();
    await this.ensureNotificationPermission();
    this.registerListeners();
    await this.fetchAndLogFcmToken();
    await this.subscribeToRecommendedTopics();
    await this.configureForegroundBehavior();
    await this.checkForPendingNotifications();

    this.hasInitialized = true;
  }

  private async ensureNotificationPermission(): Promise<void> {
    try {
      const status: PermissionStatus = await PushNotifications.checkPermissions();
      if (status.receive !== 'granted') {
        const req = await PushNotifications.requestPermissions();
        this.logger.info('Push permission result:', req);
      }
      await this.requestMessagingPermissionsIos();
    } catch (error) {
      this.logger.error('Error solicitando permisos de notificación', error);
    }
  }

  private async requestMessagingPermissionsIos(): Promise<void> {
    try {
      if (Capacitor.getPlatform() === 'ios') {
        await FirebaseMessaging.requestPermissions();
      }
    } catch (error) {
      this.logger.error('Error solicitando permisos de FirebaseMessaging (iOS)', error);
    }
  }

  private async configureForegroundBehavior(): Promise<void> {
    try {
      // En iOS, las notificaciones push del sistema se pueden deshabilitar
      // cuando la app está en primer plano usando la configuración nativa
      if (Capacitor.getPlatform() === 'ios') {
        // La configuración se maneja a través del payload de la notificación
        // desde el servidor, no desde el cliente
        this.logger.debug('Configuración de comportamiento en primer plano para iOS');
      }
    } catch (error) {
      this.logger.error('Error configurando comportamiento en primer plano', error);
    }
  }

  private registerListeners(): void {
    FirebaseMessaging.addListener('notificationReceived', (event) => {
      // Evita doble render en iOS (dejamos el listener específico de iOS)
      if (!this.platform.is('hybrid') || Capacitor.getPlatform() === 'ios') return;

      const data =
        (event as any)?.notification?.data ??
        (event as any)?.data ??
        {};

      this.publishInAppFromPayload({
        title: event.notification?.title,
        body: event.notification?.body,
        data,
      });
    });

    FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const data = (event as any)?.notification?.data ?? (event as any)?.data;
      
      if (!this.hasInitialized || !this.initialNotificationProcessed) {
        this.isProcessingInitialNotification = true;
      }
      
      void this.handleNotificationAction(data).then(() => {
        this.initialNotificationProcessed = true;
        this.isProcessingInitialNotification = false;
      }).catch(() => {
        this.isProcessingInitialNotification = false;
      });
    });

    // Agregar listener específico para iOS en primer plano
    if (Capacitor.getPlatform() === 'ios') {
      this.registerIosForegroundListener();
      this.registerIosAppStateListener();
      this.registerIosNotificationTapListener();
    }
  }

  private registerIosForegroundListener(): void {
    // Listener adicional para notificaciones push en iOS
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      if (this.platform.is('hybrid')) {
        // NO procesar la notificación in-app en iOS
        // Dejar que se muestre la notificación push nativa del sistema
        // this.processNotificationForIos(notification);
      }
    });
  }

  private processNotificationForIos(notification: any): void {
    const data = notification?.data ?? {};
    console.log('notification ----------------------', notification);

    this.publishInAppFromPayload({
      title: notification?.title,
      body: notification?.body,
      data,
    });
  }

  private registerIosAppStateListener(): void {
    App.addListener('appStateChange', async (state: AppState) => {
      if (state.isActive && this.pendingNotificationData) {
        await this.handleNotificationAction(this.pendingNotificationData);
        this.pendingNotificationData = null;
      }
    });

    App.addListener('appUrlOpen', (event) => {
    });
  }

  private registerIosNotificationTapListener(): void {
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      const data = notification?.notification?.data ?? (notification as any)?.data ?? {};

      if (!this.hasInitialized || !this.initialNotificationProcessed) {
        this.isProcessingInitialNotification = true;
      }

      if (data.action === 'open_url' && data.url) {
        void this.handleNotificationAction(data).then(() => {
          this.initialNotificationProcessed = true;
          this.isProcessingInitialNotification = false;
        });
      } else if (data.category === 'turnstile' || 
                 (data.type_notification && data.type_notification.startsWith('turnstile_')) ||
                 (data.turnstile_id !== undefined && data.event_id !== undefined)) {
        void this.handleNotificationAction(data).then(() => {
          this.initialNotificationProcessed = true;
          this.isProcessingInitialNotification = false;
        });
      } else if (data.type_notification === 'report_event' || 
                 data.type_notification === 'ecommerce_sale' ||
                 data.type_notification === 'new_event_enabled' ||
                 data.type_notification === 'new_product_enabled' ||
                 data.type_notification === 'custom_message') {
        void this.handleNotificationAction(data).then(() => {
          this.initialNotificationProcessed = true;
          this.isProcessingInitialNotification = false;
        });
      } else {
        this.pendingNotificationData = data;
        this.isProcessingInitialNotification = false;
      }
    });
  }

  private async checkForPendingNotifications(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        this.isProcessingInitialNotification = true;
        
        // En iOS, verificar notificaciones entregadas
        if (Capacitor.getPlatform() === 'ios') {
          const pendingNotifications = await this.getDeliveredNotifications();

          if (pendingNotifications.notifications && pendingNotifications.notifications.length > 0) {
            const latestNotification = pendingNotifications.notifications[0];
            const data = latestNotification.data || {};

            // iOS removes a notification from this list when it is tapped, so whatever is here
            // was not tapped: route as before, but never mark it as read or open custom_message.
            if (data.action === 'open_url' && data.url) {
              await this.handleNotificationAction(data, { markAsRead: false });
              this.initialNotificationProcessed = true;
            } else if (data.category === 'turnstile' || 
                       (data.type_notification && data.type_notification.startsWith('turnstile_')) ||
                       (data.turnstile_id !== undefined && data.event_id !== undefined)) {
              await this.handleNotificationAction(data, { markAsRead: false });
              this.initialNotificationProcessed = true;
            } else if (data.type_notification === 'report_event' || 
                       data.type_notification === 'ecommerce_sale' ||
                       data.type_notification === 'new_event_enabled' ||
                       data.type_notification === 'new_product_enabled') {
              await this.handleNotificationAction(data, { markAsRead: false });
              this.initialNotificationProcessed = true;
            }
          }
        }
        
        this.isProcessingInitialNotification = false;
      }
    } catch (error) {
      this.logger.error('Error verificando notificaciones pendientes:', error);
      this.isProcessingInitialNotification = false;
    }
  }

  // Seam for tests: Capacitor plugins are proxies that jasmine cannot spy on.
  private getDeliveredNotifications() {
    return PushNotifications.getDeliveredNotifications();
  }

  private publishInAppFromPayload(src: { title?: string; body?: string; data?: any }): void {
    const data = src?.data ?? {};
    const rawUnread = data?.unread_count ?? data?.unreadCount ?? data?.unread;
    const unreadParsed = rawUnread === undefined ? undefined : Number(String(rawUnread));
    const notifId = data?.notification_id ?? data?.notificationId;

    const inApp: InAppNotification = {
      title: src?.title || 'Nueva notificación',
      body: src?.body || '',
      data,
      timestamp: new Date(),
      unreadCount: Number.isFinite(unreadParsed) ? unreadParsed : undefined,
      notificationId: notifId ? String(notifId) : undefined,
    };

    this.ngZone.run(() => this.notificationSubject.next(inApp));
  }



  private async fetchAndLogFcmToken(): Promise<void> {
    try {
      const { token } = await FirebaseMessaging.getToken();
      if (token) {
        this.logger.info('FCM token actual:', token);
      } else {
        this.logger.warn('No se obtuvo token FCM');
      }
    } catch (error) {
      this.logger.error('Error obteniendo token FCM', error);
    }
  }


  private async handleNotificationAction(data: any, options: { markAsRead: boolean } = { markAsRead: true }): Promise<void> {
    try {
      if (!data) {
        return;
      }

      if (options.markAsRead) {
        this.markNotificationAsReadFromPush(data);
      }

      const action = data.action ?? data['action'];
      if (action === 'open_url') {
        const url: string | undefined = data.url ?? data['url'];
        if (!url) {
          this.logger.warn('Acción open_url sin url');
          return;
        }

        await Browser.open({
          url,
          windowName: '_system'
        });
        return;
      }

      const category = data.category ?? data['category'];
      const typeNotification = data.type_notification ?? data['type_notification'];
      const eventId = data.event_id ?? data['event_id'];

      const isTurnstileNotification = category === 'turnstile' || (typeNotification && typeNotification.startsWith('turnstile_')) || (data.turnstile_id !== undefined && eventId !== undefined);

      if (isTurnstileNotification) {
        if (!typeNotification) {
          this.logger.error('Notificación de turnstile sin type_notification', data);
          return;
        }

        const notification: Partial<InboxNotification> = {
          category: 'turnstile' as NotificationCategory,
          type_notification: typeNotification as NotificationType,
          event: eventId ? { 
            id: Number(eventId),
            name: data.event_name || '',
            cover_image: ''
          } : undefined,
          data: this.buildTurnstileData(data)
        };

        if (!eventId) {
          this.ngZone.run(() => {
            this.router.navigate(['/inbox']);
          });
          return;
        }

        this.ngZone.run(() => {
          this.notificationNavigation.navigateToNotificationTarget(notification as InboxNotification);
        });
        return;
      }

      if (typeNotification === 'report_event') {
        if (!eventId) {
          this.ngZone.run(() => {
            this.router.navigate(['/inbox']);
          });
          return;
        }

        const notification: Partial<InboxNotification> = {
          category: 'reports' as NotificationCategory,
          type_notification: 'report_event' as NotificationType,
          event: { 
            id: Number(eventId),
            name: data.event_name || '',
            cover_image: ''
          },
          data: {
            event_id: eventId,
            seller_id: data.seller_id ?? null
          }
        };

        this.ngZone.run(() => {
          this.notificationNavigation.navigateToNotificationTarget(notification as InboxNotification);
        });
        return;
      }

      if (typeNotification === 'ecommerce_sale') {
        if (!eventId) {
          this.ngZone.run(() => {
            this.router.navigate(['/inbox']);
          });
          return;
        }

        const notification: Partial<InboxNotification> = {
          category: 'sales' as NotificationCategory,
          type_notification: 'ecommerce_sale' as NotificationType,
          event: { 
            id: Number(eventId),
            name: data.event_name || '',
            cover_image: ''
          },
          data: {
            ticket_id: data.ticket_id ?? null,
            event_id: data.event_id ?? null
          }
        };

        this.ngZone.run(() => {
          this.notificationNavigation.navigateToNotificationTarget(notification as InboxNotification);
        });
        return;
      }

      if (typeNotification === 'custom_message') {
        if (!eventId) {
          this.ngZone.run(() => {
            this.router.navigate(['/inbox']);
          });
          return;
        }

        // shortcut: product_id is ignored, the app has no product-level screen for sellers
        const notification: Partial<InboxNotification> = {
          type_notification: 'custom_message' as NotificationType,
          event: { id: Number(eventId), name: data.event_name || '', cover_image: '' }
        };

        this.ngZone.run(() => {
          this.notificationNavigation.navigateToNotificationTarget(notification as InboxNotification);
        });
        return;
      }

      if (typeNotification === 'new_event_enabled' || typeNotification === 'new_product_enabled') {
        this.ngZone.run(() => {
          this.router.navigate(['/inbox']);
        });
        return;
      }

    } catch (error) {
      this.logger.error('Error manejando acción de notificación', error);
    }
  }

  // Fire-and-forget: a failed request (offline, no session) must never block navigation.
  private markNotificationAsReadFromPush(data: any): void {
    const id = Number(data?.notification_id);
    if (!Number.isInteger(id) || id <= 0 || this.markedAsReadFromPush.has(id)) {
      return;
    }
    this.markedAsReadFromPush.add(id);

    const onError = (error: unknown) => {
      // Forget the id so a later tap can retry.
      this.markedAsReadFromPush.delete(id);
      this.logger.warn('No se pudo marcar la notificación como leída', error);
    };

    try {
      const context = new HttpContext().set(SKIP_UNAUTHORIZED_LOGOUT, true);
      this.notificationState.markAsRead(id, context).subscribe({
        next: (response) => {
          this.notificationState.handleMarkAsReadResponse(response, id);
          void this.injector.get(NotificationBadgeService).decrementCount();
        },
        error: onError
      });
    } catch (error) {
      onError(error);
    }
  }

  private buildTurnstileData(data: any): any {
    return {
      scan_id: data.scan_id ?? null,
      status: data.status ?? null,
      message: data.message ?? null,
      assistant_uuid: data.assistant_uuid ?? null,
      assistant_dni: data.assistant_dni ?? null,
      assistant_name: data.assistant_name ?? null,
      scanned_at: data.scanned_at ?? null,
      turnstile_id: data.turnstile_id ?? null,
      turnstile_name: data.turnstile_name ?? null,
      turnstile_mac_address: data.turnstile_mac_address ?? null,
      event_id: data.event_id ?? null,
      event_name: data.event_name ?? null,
      disconnected_at: data.disconnected_at ?? null
    };
  }

  async subscribeToTopic(topic: string): Promise<void> {
    try {
      await FirebaseMessaging.subscribeToTopic({ topic });
      this.logger.info('Suscripción a topic OK:', topic);
    } catch (error) {
      this.logger.error('Error al suscribirse al topic:', topic, error);
      throw error;
    }
  }

  async unsubscribeFromTopic(topic: string): Promise<void> {
    try {
      await FirebaseMessaging.unsubscribeFromTopic({ topic });
      this.logger.info('Desuscripción de topic OK:', topic);
      console.log('unsubscribeFromTopic OK ================>', topic);
    } catch (error) {
      this.logger.error('Error al desuscribirse del topic:', topic, error);
      console.log('unsubscribeFromTopic ERROR ================>', error);
      throw error;
    }
  }


  async subscribeToRecommendedTopics(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      const shouldUpdate = await this.shouldUpdateTopicsCache();
      if (!shouldUpdate) {
        this.logger.debug('Topics cache aún válido, omitiendo consulta a API');
        return;
      }

      const deviceId = await getServerDeviceId();
      if (!deviceId) {
        this.logger.warn('No hay device_id disponible, omitiendo suscripción a topics');
        return;
      }

      const response = await this.topicApiService.getUnsubscribedTopics(parseInt(deviceId, 10)).toPromise();
      console.log('response ================>', response);

      const topics = response?.data?.unsubscribed_topics || [];

      if (!topics || topics.length === 0) {
        await this.updateTopicsCacheTimestamp();
        return;
      }

      const subscriptionResults = await Promise.allSettled(
        topics.map(topic => this.subscribeToTopic(topic))
      );

      const successfulTopics: string[] = [];
      const failedTopics: string[] = [];

      subscriptionResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulTopics.push(topics[index]);
        } else {
          failedTopics.push(topics[index]);
          this.logger.error(`Error suscribiendo a topic ${topics[index]}:`, result.reason);
        }
      });

      if (successfulTopics.length > 0) {
        await this.notifyApiAboutSubscriptions(successfulTopics);
      }

      await this.updateTopicsCacheTimestamp();

      this.logger.info(`Suscripción completada: ${successfulTopics.length} exitosas, ${failedTopics.length} fallidas`);

    } catch (error) {
      this.logger.error('Error en suscripción a topics recomendados:', error);
    }
  }


  private async shouldUpdateTopicsCache(): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key: 'topics_cache_timestamp' });

      if (!value) {
        return true;
      }

      const lastUpdate = parseInt(value, 10);
      const now = Date.now();
      const sixHoursInMs = 6 * 60 * 60 * 1000;
      const shouldUpdate = (now - lastUpdate) >= sixHoursInMs;
      return shouldUpdate;
    } catch (error) {
      this.logger.error('Error verificando cache de topics:', error);
      return true;
    }
  }


  private async updateTopicsCacheTimestamp(): Promise<void> {
    try {
      await Preferences.set({
        key: 'topics_cache_timestamp',
        value: Date.now().toString()
      });
    } catch (error) {
      this.logger.error('Error actualizando timestamp de cache:', error);
    }
  }

  private async notifyApiAboutSubscriptions(topics: string[]): Promise<void> {
    try {
      const deviceId = await getServerDeviceId();
      const request = {
        topics,
        device_id: deviceId ? parseInt(deviceId, 10) : undefined
      };

      const response = await this.topicApiService.subscribeToTopics(request).toPromise();

      if (response?.success) {
        this.logger.info('API notificada exitosamente sobre suscripciones:', topics);
      } else {
        this.logger.warn('API no confirmó suscripciones:', response?.message);
      }
    } catch (error) {
      this.logger.error('Error notificando a API sobre suscripciones:', error);
    }
  }

  async clearTopicsCache(): Promise<void> {
    try {
      
      const beforeValue = await Preferences.get({ key: 'topics_cache_timestamp' });
      await Preferences.remove({ key: 'topics_cache_timestamp' });
      const afterValue = await Preferences.get({ key: 'topics_cache_timestamp' });
      
      this.logger.info('Cache de topics limpiado');
    } catch (error) {
      this.logger.error('Error limpiando cache de topics:', error);
    }
  }

  async unsubscribeAllTopicsOnLogout(): Promise<void> {
    try {
      
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      const deviceId = await getServerDeviceId();
      
      if (!deviceId) {
        this.logger.warn('No hay device_id disponible, omitiendo desuscripción de topics');
        return;
      }

      const subscribedTopics = await this.topicApiService.getSubscribedTopics(parseInt(deviceId, 10)).toPromise() || [];

      if (subscribedTopics.length === 0) {
        this.logger.info('No hay topics suscritos para desuscribir');
        return;
      }

      this.logger.info('Topics suscritos encontrados:', subscribedTopics);

      const unsubscribeResults = await Promise.allSettled(
        subscribedTopics.map(topic => this.unsubscribeFromTopic(topic))
      );

      const successfulTopics: string[] = [];
      const failedTopics: string[] = [];

      unsubscribeResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulTopics.push(subscribedTopics[index]);
        } else {
          failedTopics.push(subscribedTopics[index]);
          this.logger.error(`Error desuscribiendo topic ${subscribedTopics[index]}:`, result.reason);
        }
      });

      if (successfulTopics.length > 0) {
        await this.topicApiService.unsubscribeDevice({ device_id: parseInt(deviceId, 10) }).toPromise();
        this.logger.info('Dispositivo marcado como inactivo en el servidor');
      }

      await this.clearTopicsCache();

      this.logger.info(`Desuscripción completada: ${successfulTopics.length} exitosas, ${failedTopics.length} fallidas`);

    } catch (error) {
      this.logger.error('Error en desuscripción completa de topics:', error);
    }
  }

  getIsProcessingInitialNotification(): boolean {
    return this.isProcessingInitialNotification;
  }

  hasProcessedInitialNotification(): boolean {
    return this.initialNotificationProcessed;
  }

  async waitForInitialNotificationProcessing(timeoutMs: number = 2000): Promise<boolean> {
    const startTime = Date.now();
    
    while (this.isProcessingInitialNotification && (Date.now() - startTime) < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return this.initialNotificationProcessed;
  }

}


